// OWNER: Agent C. Add/edit/delete accounts and holdings — PLAN.md always
// assumed these get created by SQL directly against D1 ("no signup page"),
// but the API contract (POST/PATCH/DELETE /api/accounts, /api/holdings) is
// frozen and ready, and there was no way to do this from the app at all.
// Lives inside Admin, which is already RequireAdmin-gated at the route level.
import { useState } from "react";
import { Text } from "@cloudflare/kumo/components/text";
import { Badge } from "@cloudflare/kumo/components/badge";
import { Button } from "@cloudflare/kumo/components/button";
import { Table } from "@cloudflare/kumo/components/table";
import { Empty } from "@cloudflare/kumo/components/empty";
import { LayerCard } from "@cloudflare/kumo/components/layer-card";
import { Dialog } from "@cloudflare/kumo/components/dialog";
import { Input } from "@cloudflare/kumo/components/input";
import { Select } from "@cloudflare/kumo/components/select";
import { useKumoToastManager } from "@cloudflare/kumo/components/toast";
import { PencilSimpleIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { formatMoney, formatUnits } from "@shared/format";
import { parseAmountInput } from "../../lib/numberInput";
import type { Account, AccountKind, Country, Currency, Holding, SymbolType, ValuationMode } from "@shared/types";
import {
  useAllHoldingsByAccount,
  createAccount,
  updateAccount,
  deleteAccount,
  createHolding,
  updateHolding,
  deleteHolding,
  type CreateAccountBody,
  type CreateHoldingBody,
} from "../../lib/hooks";
import { TableSkeleton } from "../../components/Skeletons";

const CURRENCY_ITEMS: Record<Currency, string> = { INR: "₹ INR", GBP: "£ GBP", USD: "$ USD" };
const COUNTRY_ITEMS: Record<Country, string> = { IN: "India", UK: "United Kingdom" };
const KIND_ITEMS: Record<AccountKind, string> = {
  MUTUAL_FUND: "Mutual Fund",
  ESOP: "ESOP",
  CASH_ISA: "Cash ISA",
  STOCKS_ISA: "Stocks ISA",
};
const VALUATION_ITEMS: Record<ValuationMode, string> = {
  UNITS_X_PRICE: "Units × price",
  CASH_BALANCE: "Cash balance",
  MANUAL_TOTAL: "Manual total",
};
const SYMBOL_TYPE_ITEMS: Record<SymbolType, string> = { NONE: "None", MF_SCHEME_CODE: "MF scheme code", TICKER: "Ticker" };

type AccountDialogState = { mode: "create" } | { mode: "edit"; account: Account } | null;
type HoldingDialogState = { accountId: number; accountCurrency: Currency; initial?: Holding } | null;
type DeleteTarget = { kind: "account" | "holding"; id: number; name: string } | null;

export function AccountsManager() {
  const query = useAllHoldingsByAccount();
  const toasts = useKumoToastManager();
  const [accountDialog, setAccountDialog] = useState<AccountDialogState>(null);
  const [holdingDialog, setHoldingDialog] = useState<HoldingDialogState>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);
  const [deleting, setDeleting] = useState(false);

  function refreshed(message: string) {
    toasts.add({ title: message, variant: "success" });
    query.reload();
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.kind === "account") await deleteAccount(deleteTarget.id);
      else await deleteHolding(deleteTarget.id);
      refreshed(`Deleted ${deleteTarget.name}`);
      setDeleteTarget(null);
    } catch (e) {
      toasts.add({ title: "Couldn't delete", content: e instanceof Error ? e.message : undefined, variant: "error" });
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Text variant="heading" as="h2">
          Accounts &amp; holdings
        </Text>
        <Button variant="secondary" icon={<PlusIcon />} onClick={() => setAccountDialog({ mode: "create" })}>
          Add account
        </Button>
      </div>

      {query.status === "loading" && <TableSkeleton rows={4} />}
      {query.status === "error" && <Text variant="error">{query.error.message}</Text>}
      {query.status === "success" && query.data.accounts.length === 0 && (
        <Empty
          size="sm"
          title="No accounts yet"
          description="Add an account (a platform or grant pool), then add holdings inside it."
        />
      )}

      {query.status === "success" &&
        query.data.accounts.map((account) => {
          const holdings = query.data.holdingsByAccount.get(account.id) ?? [];
          return (
            <LayerCard key={account.id} className="p-0">
              <LayerCard.Secondary className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span>{account.name}</span>
                  <Badge variant="neutral">{COUNTRY_ITEMS[account.country]}</Badge>
                  <Badge variant="outline">{KIND_ITEMS[account.kind]}</Badge>
                  <Badge variant="neutral">{account.currency}</Badge>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    shape="square"
                    variant="ghost"
                    icon={PencilSimpleIcon}
                    aria-label={`Edit ${account.name}`}
                    onClick={() => setAccountDialog({ mode: "edit", account })}
                  />
                  <Button
                    size="sm"
                    shape="square"
                    variant="secondary-destructive"
                    icon={TrashIcon}
                    aria-label={`Delete ${account.name}`}
                    onClick={() => setDeleteTarget({ kind: "account", id: account.id, name: account.name })}
                  />
                </div>
              </LayerCard.Secondary>
              <LayerCard.Primary className="p-0">
                {holdings.length === 0 ? (
                  <div className="p-4">
                    <Text variant="secondary" size="sm">
                      No holdings in this account yet.
                    </Text>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <Table.Header>
                        <Table.Row>
                          <Table.Head>Holding</Table.Head>
                          <Table.Head>Mode</Table.Head>
                          <Table.Head>Symbol</Table.Head>
                          <Table.Head>Balance</Table.Head>
                          <Table.Head />
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {holdings.map((h) => (
                          <Table.Row key={h.id}>
                            <Table.Cell>{h.name}</Table.Cell>
                            <Table.Cell>{VALUATION_ITEMS[h.valuationMode]}</Table.Cell>
                            <Table.Cell>{h.symbol ?? "—"}</Table.Cell>
                            <Table.Cell>
                              {h.valuationMode === "CASH_BALANCE"
                                ? formatMoney(h.cashBalance, h.currency)
                                : h.valuationMode === "MANUAL_TOTAL"
                                  ? formatMoney(h.manualValue ?? 0, h.currency)
                                  : `${formatUnits(h.units)} units`}
                            </Table.Cell>
                            <Table.Cell>
                              <div className="flex justify-end gap-2">
                                <Button
                                  size="sm"
                                  shape="square"
                                  variant="ghost"
                                  icon={PencilSimpleIcon}
                                  aria-label={`Edit ${h.name}`}
                                  onClick={() => setHoldingDialog({ accountId: account.id, accountCurrency: account.currency, initial: h })}
                                />
                                <Button
                                  size="sm"
                                  shape="square"
                                  variant="secondary-destructive"
                                  icon={TrashIcon}
                                  aria-label={`Delete ${h.name}`}
                                  onClick={() => setDeleteTarget({ kind: "holding", id: h.id, name: h.name })}
                                />
                              </div>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                )}
                <div className="p-3">
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={<PlusIcon />}
                    onClick={() => setHoldingDialog({ accountId: account.id, accountCurrency: account.currency })}
                  >
                    Add holding
                  </Button>
                </div>
              </LayerCard.Primary>
            </LayerCard>
          );
        })}

      <AccountFormDialog
        // Remounts the whole dialog (and its useState initializers below) per distinct
        // target — see the comment on its own key={openKey} for why this can't live one
        // level down on just <Dialog.Root>.
        key={`${accountDialog != null}-${accountDialog?.mode === "edit" ? accountDialog.account.id : "new"}`}
        state={accountDialog}
        onClose={() => setAccountDialog(null)}
        onSaved={() => refreshed(accountDialog?.mode === "edit" ? "Account updated" : "Account created")}
      />
      <HoldingFormDialog
        key={`${holdingDialog != null}-${holdingDialog?.initial?.id ?? "new"}`}
        state={holdingDialog}
        onClose={() => setHoldingDialog(null)}
        onSaved={() => refreshed(holdingDialog?.initial ? "Holding updated" : "Holding created")}
      />

      <Dialog.Root open={deleteTarget != null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <Dialog className="p-6">
          <Dialog.Title className="text-lg font-semibold">Delete {deleteTarget?.name}?</Dialog.Title>
          <Text variant="secondary" size="sm">
            {deleteTarget?.kind === "account"
              ? "This also deletes every holding and transaction inside this account."
              : "This also deletes every transaction recorded against this holding."}{" "}
            This can't be undone.
          </Text>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" loading={deleting} onClick={confirmDelete}>
              Delete
            </Button>
          </div>
        </Dialog>
      </Dialog.Root>
    </div>
  );
}

function AccountFormDialog({
  state,
  onClose,
  onSaved,
}: {
  state: AccountDialogState;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = state?.mode === "edit" ? state.account : null;
  // Base UI only calls onOpenChange for internally-triggered closes (Escape,
  // backdrop, the Cancel button) — not when a parent flips `open` externally
  // — so there's no reliable "just opened" hook to reset fields from. Keying
  // Dialog.Root below forces a full remount per distinct target instead,
  // and these initializers read straight from `editing` at that mount.
  const [name, setName] = useState(editing?.name ?? "");
  const [country, setCountry] = useState<Country>(editing?.country ?? "IN");
  const [kind, setKind] = useState<AccountKind>(editing?.kind ?? "MUTUAL_FUND");
  const [currency, setCurrency] = useState<Currency>(editing?.currency ?? "INR");
  const [provider, setProvider] = useState(editing?.provider ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = state != null;
  const openKey = `${open}-${editing?.id ?? "new"}`;

  function handleOpenChange(next: boolean) {
    if (!next) onClose();
  }

  async function handleSubmit() {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body: CreateAccountBody = { name: name.trim(), country, kind, currency, provider: provider.trim() || null, sortOrder: 0 };
      if (editing) await updateAccount(editing.id, body);
      else await createAccount(body);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog.Root key={openKey} open={open} onOpenChange={handleOpenChange}>
      <Dialog className="p-6">
        <Dialog.Title className="text-lg font-semibold">{editing ? "Edit account" : "Add account"}</Dialog.Title>
        <div className="mt-4 space-y-4">
          <Input label="Name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Zerodha Coin" />
          <div className="grid grid-cols-2 gap-4">
            <Select label="Country" value={country} onValueChange={(v) => setCountry((v as Country) ?? "IN")} items={COUNTRY_ITEMS} />
            <Select label="Kind" value={kind} onValueChange={(v) => setKind((v as AccountKind) ?? "MUTUAL_FUND")} items={KIND_ITEMS} />
          </div>
          <Select label="Currency" value={currency} onValueChange={(v) => setCurrency((v as Currency) ?? "INR")} items={CURRENCY_ITEMS} />
          <Input
            label="Provider"
            required={false}
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            placeholder="Optional — e.g. Zerodha, Chase UK"
          />
        </div>
        {error && (
          <div className="mt-3">
            <Text variant="error" size="sm">
              {error}
            </Text>
          </div>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={submitting} onClick={handleSubmit}>
            {editing ? "Save changes" : "Create account"}
          </Button>
        </div>
      </Dialog>
    </Dialog.Root>
  );
}

function HoldingFormDialog({
  state,
  onClose,
  onSaved,
}: {
  state: HoldingDialogState;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = state?.initial ?? null;
  // See the comment on AccountFormDialog's initializers — same reasoning.
  const [name, setName] = useState(editing?.name ?? "");
  const [valuationMode, setValuationMode] = useState<ValuationMode>(editing?.valuationMode ?? "UNITS_X_PRICE");
  const [symbolType, setSymbolType] = useState<SymbolType>(editing?.symbolType ?? "NONE");
  const [symbol, setSymbol] = useState(editing?.symbol ?? "");
  const [units, setUnits] = useState(String(editing?.units ?? 0));
  const [investedAmount, setInvestedAmount] = useState(String(editing?.investedAmount ?? 0));
  const [cashBalance, setCashBalance] = useState(String(editing?.cashBalance ?? 0));
  const [manualValue, setManualValue] = useState(String(editing?.manualValue ?? 0));
  const [strikePrice, setStrikePrice] = useState(editing?.strikePrice != null ? String(editing.strikePrice) : "");
  const [vestedUnits, setVestedUnits] = useState(editing?.vestedUnits != null ? String(editing.vestedUnits) : "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = state != null;
  const openKey = `${open}-${editing?.id ?? "new"}`;

  function handleOpenChange(next: boolean) {
    if (!next) onClose();
  }

  async function handleSubmit() {
    if (!state) return;
    if (!name.trim()) {
      setError("Name is required");
      return;
    }

    // Amount-like fields accept comma thousands-separators (e.g. "1,20,2893.323")
    // for readability, but must be stripped to a plain number before saving —
    // otherwise the comma silently corrupts downstream valuation math.
    const parsedUnits = units.trim() ? parseAmountInput(units) : 0;
    const parsedInvestedAmount = investedAmount.trim() ? parseAmountInput(investedAmount) : 0;
    const parsedCashBalance = cashBalance.trim() ? parseAmountInput(cashBalance) : 0;
    const parsedManualValue = manualValue.trim() ? parseAmountInput(manualValue) : 0;
    const parsedStrikePrice = strikePrice.trim() ? parseAmountInput(strikePrice) : null;
    const parsedVestedUnits = vestedUnits.trim() ? parseAmountInput(vestedUnits) : null;

    if (valuationMode === "UNITS_X_PRICE" && (parsedUnits === null || parsedInvestedAmount === null)) {
      setError("Starting units and invested amount must be valid numbers");
      return;
    }
    if (valuationMode === "CASH_BALANCE" && parsedCashBalance === null) {
      setError("Starting cash balance must be a valid number");
      return;
    }
    if (valuationMode === "MANUAL_TOTAL" && parsedManualValue === null) {
      setError("Starting value must be a valid number");
      return;
    }
    if (strikePrice.trim() && parsedStrikePrice === null) {
      setError("Strike price must be a valid number");
      return;
    }
    if (vestedUnits.trim() && parsedVestedUnits === null) {
      setError("Vested units must be a valid number");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const body: CreateHoldingBody = {
        accountId: state.accountId,
        name: name.trim(),
        valuationMode,
        symbolType: symbolType === "NONE" ? null : symbolType,
        symbol: symbol.trim() || null,
        currency: state.accountCurrency,
        units: valuationMode === "UNITS_X_PRICE" ? (parsedUnits ?? 0) : 0,
        investedAmount: valuationMode === "UNITS_X_PRICE" ? (parsedInvestedAmount ?? 0) : 0,
        cashBalance: valuationMode === "CASH_BALANCE" ? (parsedCashBalance ?? 0) : 0,
        manualValue: valuationMode === "MANUAL_TOTAL" ? (parsedManualValue ?? 0) : null,
        manualPrice: null,
        strikePrice: parsedStrikePrice,
        vestedUnits: parsedVestedUnits,
        notes: null,
      };
      if (editing) await updateHolding(editing.id, body);
      else await createHolding(body);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog.Root key={openKey} open={open} onOpenChange={handleOpenChange}>
      <Dialog className="max-h-[85vh] overflow-y-auto p-6">
        <Dialog.Title className="text-lg font-semibold">{editing ? "Edit holding" : "Add holding"}</Dialog.Title>
        <div className="mt-4 space-y-4">
          <Input label="Name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Parag Parikh Flexi Cap" />

          <Select
            label="Valuation mode"
            value={valuationMode}
            onValueChange={(v) => setValuationMode((v as ValuationMode) ?? "UNITS_X_PRICE")}
            items={VALUATION_ITEMS}
          />

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Symbol type"
              required={false}
              value={symbolType}
              onValueChange={(v) => setSymbolType((v as SymbolType) ?? "NONE")}
              items={SYMBOL_TYPE_ITEMS}
            />
            <Input
              label="Symbol"
              required={false}
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              placeholder="e.g. 122639, VUSA.L"
            />
          </div>

          <Select label="Currency" disabled value={state?.accountCurrency} items={CURRENCY_ITEMS} description="Fixed to the account's currency" />

          {valuationMode === "UNITS_X_PRICE" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Starting units"
                  type="text"
                  inputMode="decimal"
                  value={units}
                  onChange={(e) => setUnits(e.target.value)}
                  placeholder="0.0000"
                />
                <Input
                  label="Starting invested amount"
                  type="text"
                  inputMode="decimal"
                  value={investedAmount}
                  onChange={(e) => setInvestedAmount(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Strike price"
                  required={false}
                  type="text"
                  inputMode="decimal"
                  value={strikePrice}
                  onChange={(e) => setStrikePrice(e.target.value)}
                  description="ESOP grants only"
                />
                <Input
                  label="Vested units"
                  required={false}
                  type="text"
                  inputMode="decimal"
                  value={vestedUnits}
                  onChange={(e) => setVestedUnits(e.target.value)}
                  description="ESOP grants only"
                />
              </div>
            </>
          )}

          {valuationMode === "CASH_BALANCE" && (
            <Input
              label="Starting cash balance"
              type="text"
              inputMode="decimal"
              value={cashBalance}
              onChange={(e) => setCashBalance(e.target.value)}
              placeholder="0.00"
            />
          )}

          {valuationMode === "MANUAL_TOTAL" && (
            <Input
              label="Starting value"
              type="text"
              inputMode="decimal"
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value)}
              placeholder="0.00"
            />
          )}
        </div>
        {error && (
          <div className="mt-3">
            <Text variant="error" size="sm">
              {error}
            </Text>
          </div>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={submitting} onClick={handleSubmit}>
            {editing ? "Save changes" : "Create holding"}
          </Button>
        </div>
      </Dialog>
    </Dialog.Root>
  );
}
